/**
 * Power of Attorney section in HR admin, logic only (both looks).
 *
 * Go-live 1 November checklist: "a section in HR called Power of Attorney
 * with dropdowns -> From who (name from HR) / to whom (name from HR)/Which
 * rolles / Validity period".
 *
 * The list is small, so it loads once and the person and status filters
 * run here. The pop-up form checks the same things the server does, so a
 * mistake shows at once; the server still decides (its sentence is shown
 * when it refuses). Never shortened to "POA" on screen: that is the Proof of
 * Address document code.
 *
 * `employees` are the active employees the dashboard already holds; a person
 * who has left still shows on (and stays on) the records that name them.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import powerOfAttorneyService from '../../services/powerOfAttorneyService';
import { useFlashMessage } from '../FlashMessage';
import { getErrorMessage } from '../../utils/errorMessages';
import { formatHrDate } from './useEmployeeCrmPartner';

export const STATUS_FILTERS = [
  { value: 'active', label: 'Active' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'expired', label: 'Expired' },
  { value: 'revoked', label: 'Revoked' },
];

export const EMPTY_FORM = {
  grantor_id: '',
  grantee_id: '',
  role_ids: [],
  valid_from: '',
  until_revoked: true,
  valid_to: '',
  notes: '',
};

const personName = (p) => p.name || p.full_name || p.display_name || p.email || '';

/** [{value, label}] of people, each once, A to Z. */
export function personOptions(employees, extra = []) {
  const byId = new Map();
  [...(employees || []), ...extra].forEach((p) => {
    if (p && p.id && !byId.has(p.id)) byId.set(p.id, { value: String(p.id), label: personName(p) });
  });
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
}

export const validUntilText = (poa) => (poa.valid_to ? formatHrDate(poa.valid_to) : 'Until revoked');

export const rolesText = (poa) => poa.roles.map((r) => r.name).join(', ');

export function formFromRecord(poa) {
  return {
    grantor_id: String(poa.grantor.id),
    grantee_id: String(poa.grantee.id),
    role_ids: poa.roles.map((r) => r.id),
    valid_from: poa.valid_from,
    until_revoked: !poa.valid_to,
    valid_to: poa.valid_to || '',
    notes: poa.notes || '',
  };
}

/** The first thing wrong with the form, in the server's words, or ''. */
export function formProblem(form) {
  if (!form.grantor_id) return 'Choose who gives the power of attorney (From who).';
  if (!form.grantee_id) return 'Choose who receives the power of attorney (To whom).';
  if (form.grantor_id === form.grantee_id) {
    return 'A person cannot give a power of attorney to themselves. Choose two different people.';
  }
  if (form.role_ids.length === 0) return 'Choose at least one role.';
  if (!form.valid_from) return 'Enter the date the power of attorney is valid from.';
  if (!form.until_revoked && !form.valid_to) return 'Enter the end date, or tick "Valid until revoked".';
  if (!form.until_revoked && form.valid_to < form.valid_from) return 'The end date cannot be before the start date.';
  return '';
}

export function payloadFromForm(form) {
  return {
    grantor_id: Number(form.grantor_id),
    grantee_id: Number(form.grantee_id),
    role_ids: form.role_ids,
    valid_from: form.valid_from,
    valid_to: form.until_revoked ? null : form.valid_to,
    notes: form.notes,
  };
}

export default function usePowerOfAttorney(employees) {
  const { showSuccess } = useFlashMessage();
  const [records, setRecords] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [personFilter, setPersonFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // The pop-up: null (closed), 'new', or the record being edited
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [revoking, setRevoking] = useState(null);
  const [revokeBusy, setRevokeBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [list, roleList] = await Promise.all([
        powerOfAttorneyService.list(),
        powerOfAttorneyService.listRoles(),
      ]);
      setRecords(list);
      setRoles(roleList);
    } catch (err) {
      setLoadError(getErrorMessage(err, "Couldn't load the powers of attorney. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => records.filter((poa) => (
    (!personFilter || String(poa.grantor.id) === personFilter || String(poa.grantee.id) === personFilter)
    && (!statusFilter || poa.status === statusFilter)
  )), [records, personFilter, statusFilter]);

  const filterPeople = useMemo(
    () => personOptions(employees, records.flatMap((poa) => [poa.grantor, poa.grantee])),
    [employees, records],
  );

  const formPeople = useMemo(
    () => personOptions(employees, editing && editing !== 'new' ? [editing.grantor, editing.grantee] : []),
    [employees, editing],
  );

  // Active roles, plus any switched-off role the record being edited already has
  const formRoles = useMemo(() => {
    const kept = editing && editing !== 'new' ? editing.roles.map((r) => r.id) : [];
    return roles.filter((r) => r.is_active || kept.includes(r.id));
  }, [roles, editing]);

  const openNew = () => {
    setEditing('new');
    setForm(EMPTY_FORM);
    setFormError('');
  };

  const openEdit = (poa) => {
    setEditing(poa);
    setForm(formFromRecord(poa));
    setFormError('');
  };

  const closeForm = () => {
    if (saving) return;
    setEditing(null);
    setFormError('');
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormError('');
  };

  const toggleRole = (roleId, on) => {
    setForm((prev) => ({
      ...prev,
      role_ids: on ? [...prev.role_ids.filter((id) => id !== roleId), roleId] : prev.role_ids.filter((id) => id !== roleId),
    }));
    setFormError('');
  };

  const save = async () => {
    const problem = formProblem(form);
    if (problem) {
      setFormError(problem);
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      if (editing === 'new') {
        await powerOfAttorneyService.create(payload);
        showSuccess('Power of attorney added');
      } else {
        await powerOfAttorneyService.update(editing.id, payload);
        showSuccess('Power of attorney updated');
      }
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(getErrorMessage(err, "Couldn't save the power of attorney. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revoking) return;
    setRevokeBusy(true);
    try {
      await powerOfAttorneyService.revoke(revoking.id);
      showSuccess('Power of attorney revoked');
      setRevoking(null);
      await load();
    } catch (err) {
      setLoadError(getErrorMessage(err, "Couldn't revoke the power of attorney. Please try again."));
      setRevoking(null);
    } finally {
      setRevokeBusy(false);
    }
  };

  return {
    loading,
    loadError,
    reload: load,
    rows,
    total: records.length,
    personFilter,
    setPersonFilter,
    statusFilter,
    setStatusFilter,
    filterPeople,
    hasFilters: Boolean(personFilter || statusFilter),
    clearFilters: () => {
      setPersonFilter('');
      setStatusFilter('');
    },
    editing,
    form,
    formPeople,
    formRoles,
    formError,
    saving,
    openNew,
    openEdit,
    closeForm,
    setField,
    toggleRole,
    save,
    revoking,
    revokeBusy,
    askRevoke: setRevoking,
    cancelRevoke: () => setRevoking(null),
    confirmRevoke,
  };
}

/**
 * The active powers of attorney one employee gives and holds, for the
 * Employee Details pop-up (read only; the section in HR admin manages them).
 */
export function useEmployeePowersOfAttorney(employeeId) {
  const [state, setState] = useState({ loading: true, given: [], held: [], error: '' });

  useEffect(() => {
    if (!employeeId) return undefined;
    let cancelled = false;
    setState({ loading: true, given: [], held: [], error: '' });
    powerOfAttorneyService.list({ employee: employeeId, status: 'active' })
      .then((list) => {
        if (cancelled) return;
        setState({
          loading: false,
          given: list.filter((poa) => poa.grantor.id === employeeId),
          held: list.filter((poa) => poa.grantee.id === employeeId),
          error: '',
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            loading: false, given: [], held: [],
            error: getErrorMessage(err, "Couldn't load the powers of attorney."),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  return state;
}

/** "Ben Holder: Sign contracts, Banking and payments (until revoked)" */
export const powerLine = (poa, otherSide) => {
  const until = poa.valid_to ? `until ${formatHrDate(poa.valid_to)}` : 'until revoked';
  return `${poa[otherSide].name}: ${rolesText(poa)} (${until})`;
};
